import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Container, Row, Col, Card, Badge, Nav, Navbar, Dropdown, Modal, Button, Form, Offcanvas, OverlayTrigger, Tooltip
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

// Section components
import UrubyirukoRukora from "./UrubyirukoRukora";
import UrubyirukoRudafiteAkazi from "./UrubyirukoRudafiteAkazi";
import AbanaMirireMibi from "./AbanaMirireMibi";
import AbakobwaBabyaye from "./AbakobwaBabyaye";
import AbagoreBatwite from "./AbagoreBatwite";
import Messaging from "./Messaging";
import Broadcasts from "./Broadcasts";
import ReportPage from "../page/ReportPage";
import AkagariIbirego from "./AkagariIbirego";
import IbiregoAkagari from "./IbiregoAkagari";
import ImicoImyifatire from "./ImicoImyifatire";
import AbanaMuMihanda from "./AbanaMuMihanda";

/* ════════════════════════════════════════════════════════════
   PREMIUM DASHBOARD STYLES - FULLY RESPONSIVE
════════════════════════════════════════════════════════════ */
const DashboardStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');

    :root {
      /* ── Government palette ── */
      --bg-main:       #0b1120;
      --bg-surface:    #111827;
      --bg-surface-2:  #1a2235;
      --bg-glass:      #141e30;
      --border:        #1e2d45;
      --border-light:  #243352;

      --text:          #f0f4ff;
      --text-secondary:#d1d9f0;
      --text-muted:    #a8b8d0;

      /* Primary — Rwanda government navy */
      --accent:        #4d8ef0;
      --accent-mid:    #3a7de8;
      --accent-light:  #1a2e50;
      --accent-dark:   #1a3a6b;

      /* Semantic */
      --success:       #22c55e;
      --success-bg:    #052e16;
      --warning:       #f59e0b;
      --warning-bg:    #1c1200;
      --danger:        #f87171;
      --danger-bg:     #2d0a0a;
      --info:          #38bdf8;
      --info-bg:       #0c1a2e;
      --purple:        #a78bfa;
      --purple-bg:     #1e1040;
      --teal:          #2dd4bf;
      --teal-bg:       #042f2e;

      /* Layout */
      --radius:        10px;
      --radius-sm:     7px;
      --radius-lg:     14px;
      --shadow:        0 1px 4px rgba(0,0,0,0.3), 0 4px 16px rgba(0,0,0,0.25);
      --shadow-lg:     0 4px 24px rgba(0,0,0,0.45);
      --transition:    all 0.2s ease;
      --transition-fast: all 0.12s ease;
      --navbar-height: 64px;
      --bottom-nav-height: 64px;
      --safe-area-bottom: env(safe-area-inset-bottom, 0px);
      --safe-area-top:    env(safe-area-inset-top, 0px);
    }

    /* ── Reset & Base ─────────────────────── */
    * {
      font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      -webkit-tap-highlight-color: transparent;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    *::-webkit-scrollbar { width: 6px; height: 6px; }
    *::-webkit-scrollbar-track { background: transparent; }
    *::-webkit-scrollbar-thumb {
      background: rgba(88, 166, 255, 0.2);
      border-radius: 10px;
    }
    *::-webkit-scrollbar-thumb:hover { background: rgba(88, 166, 255, 0.4); }

    html {
      scroll-behavior: smooth;
      -webkit-text-size-adjust: 100%;
      text-size-adjust: 100%;
    }

    body {
      background: var(--bg-main) !important;
      color: var(--text) !important;
      min-height: 100vh;
      min-height: 100dvh;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .dashboard-shell {
      background: var(--bg-main) !important;
      min-height: 100vh;
      min-height: 100dvh;
      position: relative;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
    }

    /* ── Glass Card ────────────────────────── */
    .glass-card {
      background: var(--bg-glass) !important;
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid var(--border) !important;
      border-radius: var(--radius) !important;
      box-shadow: var(--shadow);
      transition: var(--transition);
      overflow: hidden;
      color: var(--text) !important;
    }

    .glass-card:hover {
      transform: translateY(-3px);
      box-shadow: var(--shadow-lg);
      border-color: rgba(77,142,240,0.25) !important;
    }

    .glass-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
    }

    /* ── Gradient Mesh Background ──────────── */
    .dashboard-bg-mesh {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 0;
      pointer-events: none;
      overflow: hidden;
    }

    .dashboard-bg-mesh::before {
      content: '';
      position: absolute;
      top: -20%;
      right: -10%;
      width: 600px;
      height: 600px;
      background: radial-gradient(circle, rgba(88, 166, 255, 0.06) 0%, transparent 70%);
      animation: meshFloat1 20s ease-in-out infinite;
    }

    .dashboard-bg-mesh::after {
      content: '';
      position: absolute;
      bottom: -10%;
      left: -5%;
      width: 500px;
      height: 500px;
      background: radial-gradient(circle, rgba(188, 140, 255, 0.05) 0%, transparent 70%);
      animation: meshFloat2 25s ease-in-out infinite;
    }

    @keyframes meshFloat1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-50px, 80px) scale(1.1); }
    }

    @keyframes meshFloat2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(60px, -40px) scale(1.15); }
    }

    /* ── Top Navbar ────────────────────────── */
    .top-navbar {
      background: rgba(10, 15, 30, 0.97) !important;
      backdrop-filter: blur(20px) saturate(200%);
      -webkit-backdrop-filter: blur(20px) saturate(200%);
      border-bottom: 1px solid var(--border);
      z-index: 1100;
      padding: 0 1.5rem !important;
      height: var(--navbar-height);
      padding-top: var(--safe-area-top) !important;
      position: sticky;
      top: 0;
    }

    .top-navbar .container-fluid {
      height: 100%;
      display: flex;
      align-items: center;
    }

    .navbar-brand-custom {
      font-weight: 800;
      font-size: 1.3rem;
      color: white !important;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      text-decoration: none;
      white-space: nowrap;
    }

    .navbar-brand-custom .brand-icon {
      width: 38px;
      height: 38px;
      background: linear-gradient(135deg, var(--accent), var(--purple));
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      box-shadow: 0 4px 16px var(--accent-glow);
    }

    .navbar-brand-custom .brand-text {
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }

    .navbar-brand-custom .brand-subtitle {
      font-size: 0.65rem;
      font-weight: 600;
      color: #c0c8d8;
      letter-spacing: 0.5px;
    }

    /* ── Notification Bell ─────────────────── */
    .notification-btn {
      width: 42px;
      height: 42px;
      border-radius: 12px !important;
      background: rgba(255, 255, 255, 0.04) !important;
      border: 1px solid var(--border) !important;
      color: var(--text-muted) !important;
      display: flex !important;
      align-items: center;
      justify-content: center;
      transition: var(--transition);
      position: relative;
    }

    .notification-btn:hover, .notification-btn:focus {
      background: rgba(88, 166, 255, 0.1) !important;
      color: var(--accent) !important;
      border-color: rgba(88, 166, 255, 0.2) !important;
    }

    .notification-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      width: 20px;
      height: 20px;
      background: var(--danger);
      border-radius: 50%;
      font-size: 0.65rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      border: 2px solid var(--bg-main);
      animation: notifPulse 2s ease-in-out infinite;
    }

    @keyframes notifPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    /* ── Search Bar ────────────────────────── */
    .search-container {
      position: relative;
      max-width: 320px;
      flex: 1;
    }

    .search-input {
      background: rgba(255, 255, 255, 0.04) !important;
      border: 1px solid var(--border) !important;
      border-radius: 12px !important;
      color: var(--text) !important;
      padding: 0.6rem 1rem 0.6rem 2.8rem !important;
      font-size: 0.85rem !important;
      transition: var(--transition) !important;
      height: 42px;
    }

    .search-input:focus {
      background: rgba(255, 255, 255, 0.06) !important;
      border-color: rgba(88, 166, 255, 0.3) !important;
      box-shadow: 0 0 0 3px rgba(88, 166, 255, 0.1) !important;
    }

    .search-input::placeholder { color: var(--text-muted) !important; }
    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      z-index: 5;
      font-size: 0.9rem;
    }

    /* ── Profile Avatar ────────────────────── */
    .profile-avatar {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: linear-gradient(135deg, var(--accent), var(--purple));
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 1rem;
      color: white;
      cursor: pointer;
      transition: var(--transition);
      border: 2px solid transparent;
    }

    .profile-avatar:hover {
      border-color: var(--accent);
      box-shadow: 0 0 20px var(--accent-glow);
    }

    .profile-avatar.online::after {
      content: '';
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 12px;
      height: 12px;
      background: var(--success);
      border-radius: 50%;
      border: 2px solid var(--bg-main);
    }

    /* ── Sidebar ───────────────────────────── */
    .sidebar-wrapper {
      position: sticky;
      top: var(--navbar-height);
      height: calc(100dvh - var(--navbar-height));
      overflow-y: auto;
      overflow-x: hidden;
      align-self: flex-start;
    }

    .sidebar {
      background: #090e1a;
      border-right: 1px solid var(--border);
      height: 100%;
      padding: 1.25rem 0.75rem;
      display: flex;
      flex-direction: column;
    }

    .sidebar-section-title {
      font-size: 0.68rem;
      font-weight: 700;
      color: #8b9ab5;
      text-transform: uppercase;
      letter-spacing: 1.2px;
      padding: 1.2rem 1rem 0.5rem;
      margin: 0;
    }

    .sidebar .nav-link {
      color: #c8d4e8 !important;
      padding: 0.7rem 1rem;
      border-radius: var(--radius-sm);
      margin: 2px 0;
      font-weight: 500;
      font-size: 0.88rem;
      transition: var(--transition);
      display: flex;
      align-items: center;
      gap: 0.85rem;
      position: relative;
      overflow: hidden;
      border: 1px solid transparent;
    }

    .sidebar .nav-link:hover {
      background: rgba(77, 142, 240, 0.12);
      color: #ffffff !important;
      border-color: rgba(77, 142, 240, 0.15);
    }

    .sidebar .nav-link.active {
      background: linear-gradient(135deg, rgba(77, 142, 240, 0.2), rgba(58, 125, 232, 0.12)) !important;
      color: #7eb8ff !important;
      border-color: rgba(77, 142, 240, 0.3);
      font-weight: 600;
    }

    .sidebar .nav-link.active::before {
      content: '';
      position: absolute;
      left: 0;
      top: 15%;
      bottom: 15%;
      width: 3px;
      background: var(--accent);
      border-radius: 0 4px 4px 0;
    }

    .sidebar .nav-icon {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      flex-shrink: 0;
      transition: var(--transition);
    }

    .sidebar .nav-link.active .nav-icon {
      background: rgba(77, 142, 240, 0.18);
    }

    .nav-count-badge {
      margin-left: auto;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.2em 0.6em;
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.08);
      color: #c8d4e8;
      font-family: 'JetBrains Mono', monospace;
    }

    .sidebar .nav-link.active .nav-count-badge {
      background: rgba(77, 142, 240, 0.25);
      color: #7eb8ff;
    }

    .sidebar-footer {
      margin-top: auto;
      padding-top: 1rem;
      border-top: 1px solid var(--border);
    }

    .logout-sidebar-btn {
      color: #c0c8d8 !important;
      padding: 0.75rem 1rem;
      border-radius: var(--radius-sm);
      font-weight: 600;
      cursor: pointer;
      transition: var(--transition);
      display: flex;
      align-items: center;
      gap: 0.85rem;
      border: 1px solid transparent;
      background: none;
      width: 100%;
      font-size: 0.88rem;
    }

    .logout-sidebar-btn:hover {
      background: rgba(248, 81, 73, 0.08);
      color: var(--danger) !important;
      border-color: rgba(248, 81, 73, 0.15);
    }

    /* ── Content Area ──────────────────────── */
    .main-content {
      padding: 1rem 0.75rem;
      padding-bottom: calc(var(--bottom-nav-height) + var(--safe-area-bottom) + 24px);
      position: relative;
      z-index: 1;
      min-width: 0;
      overflow-x: hidden;
      background: var(--bg-main);
    }

    @media (min-width: 576px) {
      .main-content { padding: 1.25rem 1rem; }
    }

    @media (min-width: 768px) {
      .main-content { padding: 1.5rem; }
    }

    @media (min-width: 992px) {
      .main-content {
        padding: 2rem 2.5rem;
        padding-bottom: 3rem;
      }
    }

    /* ── Page Header ───────────────────────── */
    .page-header {
      margin-bottom: 2rem;
    }

    .page-header h2 {
      font-size: 1.6rem;
      font-weight: 800;
      color: #ffffff;
      margin-bottom: 0.3rem;
    }

    .page-header p {
      color: #b8c5d6;
      font-size: 0.9rem;
      margin: 0;
    }

    .greeting-wave {
      display: inline-block;
      animation: wave 2.5s ease-in-out infinite;
      transform-origin: 70% 70%;
    }

    @keyframes wave {
      0%, 60%, 100% { transform: rotate(0deg); }
      10%, 30% { transform: rotate(14deg); }
      20% { transform: rotate(-8deg); }
      40% { transform: rotate(-4deg); }
      50% { transform: rotate(10deg); }
    }

    /* ── Stat Cards ────────────────────────── */
    .stat-card {
      background: var(--bg-surface) !important;
      border-radius: var(--radius) !important;
      padding: 1.5rem;
      transition: var(--transition);
      border: 1px solid var(--border) !important;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      color: var(--text) !important;
    }

    .stat-card::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0;
      transition: var(--transition);
      border-radius: inherit;
    }

    .stat-card:hover::after {
      opacity: 1;
    }

    .stat-card:hover {
      transform: translateY(-4px);
      box-shadow: var(--shadow-lg);
    }

    .stat-card:active {
      transform: translateY(-2px) scale(0.98);
    }

    .stat-icon-wrapper {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.4rem;
      position: relative;
      flex-shrink: 0;
    }

    .stat-icon-wrapper::after {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: inherit;
      opacity: 0.3;
      filter: blur(12px);
    }

    .stat-card-title {
      font-size: 0.72rem;
      color: #9db8d8;
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 0.8px;
      margin-bottom: 0.4rem;
    }

    .stat-value {
      font-size: 1.75rem;
      font-weight: 800;
      color: #f0f6ff;
      line-height: 1;
      font-family: 'JetBrains Mono', 'Nunito Sans', monospace;
    }

    .stat-change {
      font-size: 0.75rem;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 2px 8px;
      border-radius: 20px;
      margin-top: 0.5rem;
    }

    .stat-change.positive {
      background: rgba(63, 185, 80, 0.12);
      color: var(--success);
    }

    .stat-change.negative {
      background: rgba(248, 81, 73, 0.12);
      color: var(--danger);
    }

    .stat-sparkline {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 40px;
      opacity: 0.15;
    }

    /* ── Circular Progress ─────────────────── */
    .circular-progress-wrapper {
      position: relative;
      width: 120px;
      height: 120px;
      margin: 0 auto;
    }

    .circular-progress-wrapper svg {
      width: 100%;
      height: 100%;
      transform: rotate(-90deg);
    }

    .circle-bg {
      fill: none;
      stroke: rgba(77, 142, 240, 0.12);
      stroke-width: 5;
    }

    .circle-progress {
      fill: none;
      stroke-width: 5;
      stroke-linecap: round;
      transition: stroke-dasharray 1.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .circular-progress-value {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
    }

    .circular-progress-value .value {
      font-size: 1.6rem;
      font-weight: 800;
      color: #f0f6ff;
      font-family: 'JetBrains Mono', monospace;
      line-height: 1;
    }

    .circular-progress-value .label {
      font-size: 0.65rem;
      color: #8baac8;
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    /* ── Progress Cards ────────────────────── */
    .progress-insight-card {
      border-radius: var(--radius) !important;
      overflow: hidden;
      transition: var(--transition);
    }

    .progress-insight-card:hover {
      transform: translateY(-3px);
    }

    .mini-stat-bar {
      height: 6px;
      border-radius: 3px;
      background: rgba(77, 142, 240, 0.1);
      overflow: hidden;
      margin-top: 0.75rem;
    }

    .mini-stat-bar-fill {
      height: 100%;
      border-radius: 3px;
      transition: width 1.5s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* ── Info / Action Cards ────────────────── */
    .action-card {
      padding: 1rem 1.25rem;
      border-radius: var(--radius-sm);
      background: rgba(255, 255, 255, 0.02);
      border: 1px solid var(--border);
      margin-bottom: 0.75rem;
      transition: var(--transition);
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
    }

    .action-card:hover {
      background: rgba(77, 142, 240, 0.08);
      border-color: rgba(77, 142, 240, 0.2);
      transform: translateX(4px);
    }

    .action-card:active {
      transform: translateX(2px) scale(0.99);
    }

    .action-card .action-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .action-card .action-label {
      flex: 1;
      margin-left: 1rem;
    }

    .action-card .action-label strong {
      color: #e8f0ff;
      font-size: 0.88rem;
      display: block;
    }

    .action-card .action-label small {
      color: #8baac8;
      font-size: 0.75rem;
    }

    /* ── Quick Actions Grid ────────────────── */
    .quick-action-btn {
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.25rem 1rem;
      text-align: center;
      cursor: pointer;
      transition: var(--transition);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.6rem;
      color: var(--text);
      height: 100%;
      min-height: 110px;
      justify-content: center;
    }

    .quick-action-btn:hover {
      background: rgba(77, 142, 240, 0.1);
      border-color: rgba(77, 142, 240, 0.25);
      transform: translateY(-3px);
    }

    .quick-action-btn:active {
      transform: translateY(-1px) scale(0.97);
    }

    .quick-action-btn .qa-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.15rem;
      flex-shrink: 0;
    }

    .quick-action-btn .qa-label {
      font-size: 0.72rem;
      font-weight: 600;
      color: #b8cce0;
      line-height: 1.2;
      word-break: break-word;
      text-align: center;
    }

    @media (max-width: 575.98px) {
      .quick-action-btn {
        padding: 0.9rem 0.5rem;
        min-height: 95px;
        gap: 0.4rem;
      }
      .quick-action-btn .qa-icon {
        width: 38px;
        height: 38px;
        font-size: 1rem;
        border-radius: 10px;
      }
      .quick-action-btn .qa-label {
        font-size: 0.65rem;
      }
    }

    @media (min-width: 576px) and (max-width: 767.98px) {
      .quick-action-btn {
        padding: 1rem 0.6rem;
        min-height: 100px;
      }
      .quick-action-btn .qa-icon {
        width: 42px;
        height: 42px;
      }
      .quick-action-btn .qa-label {
        font-size: 0.7rem;
      }
    }

    /* ── Bottom Nav (Mobile) ───────────────── */
    @media (max-width: 991.98px) {
      .sidebar-wrapper { display: none !important; }

      .bottom-nav {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(8, 12, 24, 0.97);
        backdrop-filter: blur(24px) saturate(200%);
        -webkit-backdrop-filter: blur(24px) saturate(200%);
        border-top: 1px solid var(--border);
        padding: 6px 0 calc(6px + var(--safe-area-bottom));
        z-index: 1050;
        display: flex !important;
        justify-content: space-around;
        align-items: stretch;
      }

      .bottom-nav-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 6px 4px 4px;
        text-decoration: none;
        color: #8b9ab5;
        font-size: 0.62rem;
        font-weight: 600;
        transition: var(--transition-fast);
        position: relative;
        border-radius: 10px;
        margin: 0 2px;
        cursor: pointer;
        -webkit-user-select: none;
        user-select: none;
      }

      .bottom-nav-item i {
        font-size: 1.3rem;
        margin-bottom: 2px;
        transition: var(--transition-fast);
      }

      .bottom-nav-item.active {
        color: var(--accent);
      }

      .bottom-nav-item.active::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 24px;
        height: 3px;
        background: var(--accent);
        border-radius: 0 0 4px 4px;
      }

      .bottom-nav-item.active i {
        transform: scale(1.15);
      }

      .bottom-nav-item:active {
        transform: scale(0.9);
      }

      .bottom-nav-item .bnav-count {
        position: absolute;
        top: 2px;
        right: calc(50% - 18px);
        min-width: 16px;
        height: 16px;
        background: var(--danger);
        border-radius: 50%;
        font-size: 0.55rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        padding: 0 3px;
        border: 1.5px solid var(--bg-main);
      }
    }

    @media (min-width: 992px) {
      .bottom-nav { display: none !important; }
    }

    /* ── FAB ────────────────────────────────── */
    .fab-wrapper {
      position: fixed;
      bottom: calc(var(--bottom-nav-height) + var(--safe-area-bottom) + 16px);
      right: 20px;
      z-index: 1000;
    }

    @media (min-width: 992px) {
      .fab-wrapper { bottom: 30px; right: 30px; }
    }

    .fab-btn {
      width: 58px;
      height: 58px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--accent), var(--accent-dark));
      color: white;
      font-size: 1.5rem;
      box-shadow: 0 8px 32px rgba(9, 105, 218, 0.4);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: var(--transition);
      position: relative;
    }

    .fab-btn::before {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--accent), var(--accent-dark));
      opacity: 0;
      filter: blur(16px);
      transition: var(--transition);
    }

    .fab-btn:hover::before { opacity: 0.5; }
    .fab-btn:hover { transform: scale(1.1); box-shadow: 0 12px 40px rgba(9, 105, 218, 0.5); }
    .fab-btn:active { transform: scale(0.95); }

    /* ── Modals ─────────────────────────────── */
    .custom-modal .modal-content {
      background: #111827 !important;
      color: #f0f4ff !important;
      border-radius: var(--radius-lg) !important;
      border: 1px solid rgba(77,142,240,0.2) !important;
      box-shadow: 0 24px 80px rgba(0,0,0,0.6);
      overflow: hidden;
    }

    .custom-modal .modal-header {
      border-bottom: 1px solid var(--border) !important;
      padding: 1.25rem 1.5rem;
    }

    .custom-modal .modal-footer {
      border-top: 1px solid var(--border) !important;
      padding: 1rem 1.5rem;
    }

    .custom-modal .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
    }

    /* ── Action Modal List Items ────────────── */
    .action-modal-item {
      background: rgba(255, 255, 255, 0.03) !important;
      border: 1px solid var(--border) !important;
      color: #d8e4f8 !important;
      border-radius: var(--radius-sm) !important;
      margin-bottom: 0.5rem;
      padding: 1rem 1.25rem !important;
      transition: var(--transition);
      cursor: pointer;
    }

    .action-modal-item:hover,
    .action-modal-item:focus {
      background: rgba(77, 142, 240, 0.1) !important;
      border-color: rgba(77, 142, 240, 0.25) !important;
      color: #7eb8ff !important;
      transform: translateX(4px);
    }

    .action-modal-item:active {
      transform: translateX(2px) scale(0.99);
    }

    /* ── Mobile Offcanvas Sidebar ──────────── */
    .mobile-sidebar .offcanvas-body {
      background: #090e1a;
      padding: 0;
    }

    .mobile-sidebar .offcanvas-header {
      background: #090e1a;
      border-bottom: 1px solid var(--border);
    }

    .mobile-sidebar .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
    }

    /* ── Loading Screen ────────────────────── */
    .loading-screen {
      position: fixed;
      inset: 0;
      background: #0b1120;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    }

    .loading-screen .loader-ring {
      width: 60px;
      height: 60px;
      border: 3px solid var(--border);
      border-top-color: var(--accent);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      100% { transform: rotate(360deg); }
    }

    .loading-screen .loader-text {
      color: #a8b8d0;
      font-size: 0.9rem;
      margin-top: 1.5rem;
      font-weight: 500;
    }

    .loading-screen .loader-bar {
      width: 200px;
      height: 3px;
      background: var(--border);
      border-radius: 2px;
      margin-top: 1rem;
      overflow: hidden;
    }

    .loading-screen .loader-bar::after {
      content: '';
      display: block;
      width: 40%;
      height: 100%;
      background: linear-gradient(90deg, var(--accent), var(--purple));
      border-radius: 2px;
      animation: loaderSlide 1.5s ease-in-out infinite;
    }

    @keyframes loaderSlide {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(350%); }
    }

    /* ── Pull-to-refresh indicator ─────────── */
    .pull-indicator {
      position: fixed;
      top: calc(var(--navbar-height) + 10px);
      left: 50%;
      transform: translateX(-50%) translateY(-60px);
      z-index: 1200;
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: var(--shadow);
      transition: transform 0.3s ease;
    }

    .pull-indicator.visible {
      transform: translateX(-50%) translateY(0);
    }

    /* ── Responsive Typography ─────────────── */
    @media (max-width: 575.98px) {
      .page-header h2 { font-size: 1.3rem; }
      .stat-value { font-size: 1.4rem; }
      .stat-card { padding: 1.2rem; }
      .stat-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
      .main-content { padding: 1rem 0.75rem; }
      .circular-progress-wrapper { width: 90px; height: 90px; }
      .circular-progress-value .value { font-size: 1.3rem; }
    }

    @media (min-width: 576px) and (max-width: 767.98px) {
      .main-content { padding: 1.25rem 1rem; }
    }

    /* ── Tablet landscape tweaks ───────────── */
    @media (min-width: 768px) and (max-width: 991.98px) {
      .main-content { padding: 1.5rem; }
    }

    /* ── Touch feedback ────────────────────── */
    @media (hover: none) and (pointer: coarse) {
      .glass-card:hover { transform: none; }
      .stat-card:hover { transform: none; }
      .stat-card:active { transform: scale(0.98); }
      .glass-card:active { transform: scale(0.99); }
      .action-card:hover { transform: none; }
      .action-card:active { transform: scale(0.98); }
    }

    /* ── Dark mode dropdown ────────────────── */
    .dark-dropdown {
      background: #111827 !important;
      border: 1px solid rgba(77,142,240,0.2) !important;
      border-radius: var(--radius) !important;
      box-shadow: var(--shadow-lg) !important;
      padding: 0.5rem !important;
      min-width: 220px;
    }

    .dark-dropdown .dropdown-item {
      color: #d8e4f8 !important;
      padding: 0.65rem 1rem !important;
      border-radius: 8px !important;
      font-size: 0.88rem;
      transition: var(--transition-fast);
    }

    .dark-dropdown .dropdown-item:hover,
    .dark-dropdown .dropdown-item:focus {
      background: rgba(77, 142, 240, 0.12) !important;
      color: #7eb8ff !important;
    }

    .dark-dropdown .dropdown-item.text-danger-custom:hover {
      background: rgba(248, 81, 73, 0.08) !important;
      color: var(--danger) !important;
    }

    .dark-dropdown .dropdown-divider {
      border-color: var(--border) !important;
      margin: 0.35rem 0 !important;
    }

    /* ── Swipe hint animation ──────────────── */
    @keyframes swipeHint {
      0%, 100% { transform: translateX(0); opacity: 0.5; }
      50% { transform: translateX(10px); opacity: 1; }
    }

    /* ── Status dot ────────────────────────── */
    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
    }

    .status-dot.online { background: var(--success); box-shadow: 0 0 8px var(--success-glow); }
    .status-dot.warning { background: var(--warning); box-shadow: 0 0 8px var(--warning-glow); }
    .status-dot.danger { background: var(--danger); box-shadow: 0 0 8px var(--danger-glow); }

    /* ── Empty state ───────────────────────── */
    .empty-state {
      text-align: center;
      padding: 3rem 2rem;
      color: var(--text-muted);
    }

    .empty-state i {
      font-size: 3rem;
      opacity: 0.3;
      margin-bottom: 1rem;
    }

    /* ── Dashboard hero greeting ───────────── */
    .page-header {
      background: linear-gradient(135deg, rgba(77,142,240,0.12) 0%, rgba(167,139,250,0.07) 100%);
      border: 1px solid rgba(77,142,240,0.2);
      border-radius: var(--radius-lg);
      padding: 1.75rem 2rem;
      margin-bottom: 2rem;
      position: relative;
      overflow: hidden;
    }

    .page-header::before {
      content: '';
      position: absolute;
      top: -40px; right: -40px;
      width: 180px; height: 180px;
      background: radial-gradient(circle, rgba(77,142,240,0.15) 0%, transparent 70%);
      pointer-events: none;
    }

    .page-header h2 {
      font-size: 1.6rem;
      font-weight: 800;
      color: #f0f6ff;
      margin-bottom: 0.3rem;
    }

    .page-header p {
      color: #a8c0d8;
      font-size: 0.9rem;
      margin: 0;
    }

    /* ── Section headings ──────────────────── */
    .section-heading {
      font-size: 0.78rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #8baac8;
      margin-bottom: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .section-heading::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--border-light);
    }

    /* ── Haptic / ripple effect ─────────────── */
    .ripple-effect {
      position: relative;
      overflow: hidden;
    }

    .ripple-effect::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      pointer-events: none;
      background-image: radial-gradient(circle, rgba(255,255,255,0.1) 10%, transparent 10.01%);
      background-repeat: no-repeat;
      background-position: 50%;
      transform: scale(10, 10);
      opacity: 0;
      transition: transform 0.4s, opacity 0.8s;
    }

    .ripple-effect:active::after {
      transform: scale(0, 0);
      opacity: 0.3;
      transition: 0s;
    }
  `}</style>
);

/* ════════════════════════════════════════════════════════════
   ANIMATED CIRCULAR PROGRESS COMPONENT
════════════════════════════════════════════════════════════ */
const CircularProgress = ({ percentage, color = "var(--accent)", size = 120, label = "%" }) => {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const cleanPercentage = Math.max(0, Math.min(100, percentage || 0));
  const offset = circumference - (cleanPercentage / 100) * circumference;

  return (
    <div className="circular-progress-wrapper" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100">
        <circle className="circle-bg" cx="50" cy="50" r={radius} />
        <motion.circle
          className="circle-progress"
          cx="50" cy="50" r={radius}
          style={{ stroke: color }}
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeInOut", delay: 0.3 }}
        />
      </svg>
      <div className="circular-progress-value">
        <motion.div
          className="value"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {cleanPercentage.toFixed(0)}<span style={{ fontSize: '0.7em' }}>{label}</span>
        </motion.div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   ANIMATED COUNTER COMPONENT
════════════════════════════════════════════════════════════ */
const AnimatedCounter = ({ value, duration = 1.5 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(value, 10) || 0;
    if (end === 0) { setCount(0); return; }

    const totalFrames = Math.round(duration * 60);
    const increment = end / totalFrames;
    let frame = 0;

    const counter = setInterval(() => {
      frame++;
      start += increment;
      if (frame >= totalFrames) {
        setCount(end);
        clearInterval(counter);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(counter);
  }, [value, duration]);

  return <span>{count}</span>;
};

/* ════════════════════════════════════════════════════════════
   STAT CARD COMPONENT
════════════════════════════════════════════════════════════ */
const StatCard = ({ icon, title, value, color, onClick, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, delay }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    style={{ cursor: 'pointer' }}
  >
    <div className="stat-card ripple-effect" style={{
      borderColor: `${color}25`,
      background: `linear-gradient(135deg, rgba(22,27,34,0.9), rgba(22,27,34,0.7))`,
      boxShadow: `0 4px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)`
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
        background: `linear-gradient(90deg, transparent, ${color}60, transparent)`
      }} />
      <div className="d-flex align-items-start gap-3">
        <div className="stat-icon-wrapper" style={{
          background: `${color}18`,
          color,
          boxShadow: `0 4px 16px ${color}25`
        }}>
          <i className={icon}></i>
        </div>
        <div className="flex-grow-1 min-w-0">
          <p className="stat-card-title">{title}</p>
          <div className="stat-value" style={{ color: 'white' }}>
            <AnimatedCounter value={value} />
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

/* ════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
════════════════════════════════════════════════════════════ */
function DashboardUmunyamabanga() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [showLogout, setShowLogout] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef(null);

  // ═══ DATA STATE ═══════════════════════════════════
  const [dataStats, setDataStats] = useState({
    youthEmployed: 0, youthUnemployed: 0, youthTotal: 0, employmentRate: 0,
    teenMothers: 0, mothersInSchool: 0, schoolReturnRate: 0,
    maternalCases: 0, maternalCheckedUp: 0,
    malnutritionCases: 0, malnourishmentUrgent: 0,
    rukoraCount: 0, rudafiteCount: 0, abakobwaCount: 0, abagoreCount: 0, abanaCount: 0
  });

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Get greeting
  const getGreeting = useCallback(() => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Mwaramutse";
    if (hour < 17) return "Mwiriwe";
    return "Mwiriwe";
  }, [currentTime]);

  const fetchData = useCallback(async (endpoint) => {
    const token = localStorage.getItem('token');
    if (!token) return [];
    try {
      const res = await axios.get(`/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data || [];
    } catch (e) {
      return [];
    }
  }, []);

  const fetchDataAndCalculateStats = useCallback(async () => {
    setLoading(true);
    setRefreshing(true);

    const [rukora, rudafite, abakobwa, abagore, abana] = await Promise.all([
      fetchData("urubyiruko_rukora"),
      fetchData("urubyiruko_rudafite_akazi"),
      fetchData("abakobwa_babyaye"),
      fetchData("abagore_batwite"),
      fetchData("abana_barimwo_mirire_mibi"),
    ]);

    const youthEmployed = rukora.length;
    const youthUnemployed = rudafite.length;
    const youthTotal = youthEmployed + youthUnemployed;
    const employmentRate = youthTotal > 0 ? (youthEmployed / youthTotal) * 100 : 0;

    const teenMothers = abakobwa.length;
    const mothersInSchool = abakobwa.filter(k => k.asubiye_mw_ishuri === 'Yego').length;
    const schoolReturnRate = teenMothers > 0 ? (mothersInSchool / teenMothers) * 100 : 0;

    const maternalCases = abagore.length;
    const maternalCheckedUp = abagore.filter(g => g.asuzumwe_kuvuzi === 'Yego').length;

    const malnutritionCases = abana.length;
    const malnourishmentUrgent = abana.filter(d => d.urwego_rw_imirire === "Bikabije" && d.afashijwe === "Oya").length;

    setDataStats({
      youthEmployed, youthUnemployed, youthTotal, employmentRate,
      teenMothers, mothersInSchool, schoolReturnRate,
      maternalCases, maternalCheckedUp, malnutritionCases, malnourishmentUrgent,
      rukoraCount: youthEmployed, rudafiteCount: youthUnemployed,
      abakobwaCount: teenMothers, abagoreCount: maternalCases, abanaCount: malnutritionCases
    });

    setLoading(false);
    setTimeout(() => setRefreshing(false), 500);
  }, [fetchData]);

  useEffect(() => {
    fetchDataAndCalculateStats();
  }, [fetchDataAndCalculateStats]);

  // Navigation items
  const navItems = useMemo(() => [
    { id: 'dashboard', icon: 'bi-grid-1x2-fill', label: 'Dashboard', color: 'var(--accent)' },
    { id: 'rukora', icon: 'bi-briefcase-fill', label: 'Urubyiruko Rukora', shortLabel: 'Rukora', count: dataStats.rukoraCount, color: 'var(--success)' },
    { id: 'rudafite', icon: 'bi-person-x-fill', label: 'Rudafite Akazi', shortLabel: 'Rudafite', count: dataStats.rudafiteCount, color: 'var(--danger)' },
    { id: 'abana', icon: 'bi-heart-pulse-fill', label: 'Abana (Mirire Mibi)', shortLabel: 'Abana', count: dataStats.abanaCount, color: 'var(--warning)' },
    { id: 'abakobwa', icon: 'bi-person-hearts', label: 'Abakobwa Babyaye', shortLabel: 'Abakobwa', count: dataStats.abakobwaCount, color: 'var(--purple)' },
    { id: 'abagore', icon: 'bi-person-standing-dress', label: 'Abagore Batwite', shortLabel: 'Abagore', count: dataStats.abagoreCount, color: 'var(--teal)' },
    { id: 'messaging', icon: 'bi-chat-dots-fill', label: 'Ubutumwa', shortLabel: 'Ubutumwa', color: 'var(--pink)' },
    { id: 'broadcasts', icon: 'bi-megaphone-fill', label: 'Amatangazo', shortLabel: 'Amatangazo', color: 'var(--cyan, #56d4dd)' },
    { id: 'reports', icon: 'bi-file-earmark-text-fill', label: 'Kwandika Raporo', shortLabel: 'Raporo', color: 'var(--indigo, #6366f1)' },
    { id: 'ibirego', icon: 'bi-file-earmark-text', label: 'Ibirego', shortLabel: 'Ibirego', color: 'var(--danger, #f87171)', group: 'Umutekano' },
    { id: 'isibo-ibirego', icon: 'bi-file-earmark-text', label: 'IBIREGO', shortLabel: 'IBIREGO', color: 'var(--success, #10b981)', group: 'Umutekano' },
    { id: 'imico-imyifatire', icon: 'bi-award-fill', label: "Imico n'Imyifatire", shortLabel: 'Imico', color: 'var(--teal, #0f766e)', group: 'Ibyangobwa' },
    { id: 'abana-mu-mihanda', icon: 'bi-signpost-split-fill', label: "Abana Bari mu Mihanda", shortLabel: 'Mihanda', color: 'var(--danger, #f87171)' },
  ], [dataStats]);

  // Bottom nav items
  const bottomNavItems = useMemo(() => [
    { id: 'dashboard', icon: 'bi-grid-1x2-fill', label: 'Home' },
    { id: 'rukora', icon: 'bi-briefcase-fill', label: 'Rukora', count: dataStats.rukoraCount },
    { id: 'messaging', icon: 'bi-chat-dots-fill', label: 'Chat' },
    { id: 'broadcasts', icon: 'bi-megaphone-fill', label: 'Tangazo' },
    { id: 'more', icon: 'bi-grid-3x3-gap-fill', label: 'Byinshi' },
  ], [dataStats]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setShowMobileSidebar(false);
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalNotifications = useMemo(() =>
    dataStats.malnourishmentUrgent + (dataStats.youthUnemployed > 10 ? 1 : 0)
  , [dataStats]);

  // ═══ LOADING SCREEN ═══════════════════════════════
  if (loading && activeTab === "dashboard") {
    return (
      <>
        <DashboardStyles />
        <motion.div
          className="loading-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div style={{
              width: 70, height: 70, borderRadius: 18,
              background: 'linear-gradient(135deg, var(--accent), var(--purple))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.8rem', color: 'white', marginBottom: '1.5rem',
              boxShadow: '0 8px 32px var(--accent-glow)',
              margin: '0 auto 1.5rem'
            }}>
              <i className="bi bi-grid-1x2-fill"></i>
            </div>
          </motion.div>
          <div className="loader-ring"></div>
          <p className="loader-text">Tegereza gato...</p>
          <div className="loader-bar"></div>
        </motion.div>
      </>
    );
  }

  // ═══ MAIN RENDER ══════════════════════════════════
  return (
    <>
      <DashboardStyles />
      <div className="dashboard-shell">
        {/* Background Mesh */}
        <div className="dashboard-bg-mesh" />

        {/* ═══════════ TOP NAVBAR ═══════════════════════════ */}
        <Navbar className="top-navbar" fixed="top">
          <Container fluid className="px-2 px-md-3">
            {/* Mobile menu button */}
            <Button
              variant="link"
              className="d-lg-none p-2 me-2 text-white"
              onClick={() => setShowMobileSidebar(true)}
              style={{ fontSize: '1.3rem', textDecoration: 'none' }}
            >
              <i className="bi bi-list"></i>
            </Button>

            {/* Brand */}
            <div className="navbar-brand-custom" onClick={() => handleTabChange('dashboard')} style={{ cursor: 'pointer' }}>
              <div className="brand-icon">
                <i className="bi bi-grid-1x2-fill"></i>
              </div>
              <div className="brand-text d-none d-sm-flex">
                <span>Dashboard</span>
                <span className="brand-subtitle">Umunyamabanga</span>
              </div>
            </div>

            {/* Search - Desktop only */}
            <div className="search-container d-none d-md-block mx-4">
              <i className="bi bi-search search-icon"></i>
              <Form.Control
                type="text"
                placeholder="Shakisha..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Right actions */}
            <div className="ms-auto d-flex align-items-center gap-2">
              {/* Refresh Button */}
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Kuvugurura</Tooltip>}>
                <motion.button
                  className="notification-btn d-none d-md-flex"
                  onClick={fetchDataAndCalculateStats}
                  whileTap={{ scale: 0.9, rotate: 180 }}
                  animate={refreshing ? { rotate: 360 } : {}}
                  transition={{ duration: 0.6 }}
                  style={{ background: 'transparent', border: '1px solid var(--border)' }}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </motion.button>
              </OverlayTrigger>

              {/* Quick access to Broadcasts from navbar */}
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Amatangazo</Tooltip>}>
                <button
                  className="notification-btn d-none d-md-flex"
                  onClick={() => handleTabChange('broadcasts')}
                >
                  <i className="bi bi-megaphone"></i>
                </button>
              </OverlayTrigger>

              {/* Quick access to Reports from navbar */}
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Kwandika Raporo</Tooltip>}>
                <button
                  className="notification-btn d-none d-md-flex"
                  onClick={() => handleTabChange('reports')}
                >
                  <i className="bi bi-file-earmark-text"></i>
                </button>
              </OverlayTrigger>

              {/* Notifications */}
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Amakuru {totalNotifications > 0 ? `(${totalNotifications})` : ''}</Tooltip>}>
                <button className="notification-btn">
                  <i className="bi bi-bell"></i>
                  {totalNotifications > 0 && (
                    <span className="notification-badge">{totalNotifications}</span>
                  )}
                </button>
              </OverlayTrigger>

              {/* Profile */}
              <Dropdown align="end">
                <Dropdown.Toggle as="div" style={{ cursor: 'pointer' }}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="profile-avatar position-relative">
                      U
                      <span className="status-dot online" style={{
                        position: 'absolute', bottom: -1, right: -1,
                        border: '2px solid var(--bg-main)'
                      }}></span>
                    </div>
                    <div className="d-none d-lg-block" style={{ lineHeight: 1.3 }}>
                      <div className="fw-bold text-white" style={{ fontSize: '0.85rem' }}>Umunyamabanga</div>
                      <div style={{ fontSize: '0.7rem', color: '#a0b0c8' }}>Admin</div>
                    </div>
                    <i className="bi bi-chevron-down d-none d-lg-block" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}></i>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="dark-dropdown mt-2">
                  <div className="px-3 py-2 mb-2" style={{ borderBottom: '1px solid var(--border)' }}>
                    <div className="fw-bold text-white" style={{ fontSize: '0.9rem' }}>Umunyamabanga</div>
                    <div style={{ fontSize: '0.75rem', color: '#a0b0c8' }}>admin@system.rw</div>
                  </div>
                  <Dropdown.Item className="d-flex align-items-center gap-2">
                    <i className="bi bi-person"></i> Umwirondoro
                  </Dropdown.Item>
                  <Dropdown.Item className="d-flex align-items-center gap-2">
                    <i className="bi bi-gear"></i> Igenamiterere
                  </Dropdown.Item>
                  {/* Report shortcut in profile dropdown */}
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2"
                    onClick={() => handleTabChange('reports')}
                  >
                    <i className="bi bi-file-earmark-text"></i> Kwandika Raporo
                  </Dropdown.Item>
                  {/* Broadcasts shortcut in profile dropdown */}
                  <Dropdown.Item
                    className="d-flex align-items-center gap-2"
                    onClick={() => handleTabChange('broadcasts')}
                  >
                    <i className="bi bi-megaphone"></i> Amatangazo
                  </Dropdown.Item>
                  <Dropdown.Item className="d-flex align-items-center gap-2">
                    <i className="bi bi-question-circle"></i> Ubufasha
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    className="text-danger-custom d-flex align-items-center gap-2"
                    onClick={() => setShowLogout(true)}
                  >
                    <i className="bi bi-box-arrow-right"></i> Gusohoka
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Container>
        </Navbar>

        {/* ═══════════ MAIN LAYOUT ═══════════════════════════ */}
        <div style={{ paddingTop: 'var(--navbar-height)' }}>
          <Container fluid className="px-0">
            <Row className="g-0">
              {/* ═══ Desktop Sidebar ═══ */}
              <Col lg={3} xl={2} className="d-none d-lg-block">
                <div className="sidebar-wrapper ">
                  <nav className="sidebar">
                    <div className="flex-grow-1">
                      <p className="sidebar-section-title">MENU NYAMUKURU</p>

                      {navItems.filter(item =>
                        !['broadcasts', 'reports'].includes(item.id)
                      ).map((item) => (
                        <Nav.Link
                          key={item.id}
                          className={`${activeTab === item.id ? 'active' : ''}`}
                          onClick={() => handleTabChange(item.id)}
                        >
                          <div className="nav-icon" style={{
                            background: activeTab === item.id ? `${item.color}15` : 'transparent',
                            color: activeTab === item.id ? item.color : 'inherit'
                          }}>
                            <i className={item.icon}></i>
                          </div>
                          <span className="flex-grow-1">{item.label}</span>
                          {item.count > 0 && (
                            <span className="nav-count-badge">{item.count}</span>
                          )}
                        </Nav.Link>
                      ))}

                      <div style={{ borderTop: '1px solid var(--border)', margin: '1rem 0' }} />

                      <p className="sidebar-section-title">BYINSHI</p>

                      {/* Amatangazo (Broadcasts) in sidebar BYINSHI section */}
                      <Nav.Link
                        className={`${activeTab === 'broadcasts' ? 'active' : ''}`}
                        onClick={() => handleTabChange('broadcasts')}
                      >
                        <div className="nav-icon" style={{
                          background: activeTab === 'broadcasts' ? 'rgba(86, 212, 221, 0.15)' : 'transparent',
                          color: activeTab === 'broadcasts' ? 'var(--cyan, #56d4dd)' : 'inherit'
                        }}>
                          <i className="bi bi-megaphone-fill"></i>
                        </div>
                        <span>Amatangazo</span>
                      </Nav.Link>

                      {/* Kwandika Raporo (Report Page) in sidebar BYINSHI section */}
                      <Nav.Link
                        className={`${activeTab === 'reports' ? 'active' : ''}`}
                        onClick={() => handleTabChange('reports')}
                      >
                        <div className="nav-icon" style={{
                          background: activeTab === 'reports' ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                          color: activeTab === 'reports' ? 'var(--indigo, #6366f1)' : 'inherit'
                        }}>
                          <i className="bi bi-file-earmark-text-fill"></i>
                        </div>
                        <span>Kwandika Raporo</span>
                      </Nav.Link>

                      <Nav.Link className="text-muted">
                        <div className="nav-icon"><i className="bi bi-bar-chart"></i></div>
                        <span>Raporo</span>
                      </Nav.Link>
                      <Nav.Link className="text-muted">
                        <div className="nav-icon"><i className="bi bi-download"></i></div>
                        <span>Gukuramo</span>
                      </Nav.Link>

                      {/* Ibirego */}
                      <Nav.Link
                        className={`${activeTab === 'ibirego' ? 'active' : ''}`}
                        onClick={() => handleTabChange('ibirego')}
                      >
                        <div className="nav-icon" style={{
                          background: activeTab === 'ibirego' ? 'rgba(248, 113, 113, 0.15)' : 'transparent',
                          color: activeTab === 'ibirego' ? 'var(--danger, #f87171)' : 'inherit'
                        }}>
                          <i className="bi bi-file-earmark-text"></i>
                        </div>
                        <span>Ibirego</span>
                      </Nav.Link>
                    </div>

                    <div className="sidebar-footer">
                      <button className="logout-sidebar-btn" onClick={() => setShowLogout(true)}>
                        <i className="bi bi-box-arrow-right fs-5"></i>
                        <span>Gusohoka</span>
                      </button>
                    </div>
                  </nav>
                </div>
              </Col>

              {/* ═══ Main Content ═══ */}
              <Col lg={9} xl={10}>
                <div className="main-content" ref={contentRef}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      {activeTab === "dashboard" ? (
                        <DashboardContent
                          dataStats={dataStats}
                          navItems={navItems}
                          greeting={getGreeting()}
                          onTabChange={handleTabChange}
                          onRefresh={fetchDataAndCalculateStats}
                          refreshing={refreshing}
                        />
                      ) : (
                        <div>
                          {/* Back button for mobile */}
                          <div className="d-lg-none mb-3">
                            <Button
                              variant="link"
                              className="text-white p-0 d-flex align-items-center gap-2"
                              style={{ textDecoration: 'none', fontSize: '0.9rem' }}
                              onClick={() => handleTabChange('dashboard')}
                            >
                              <i className="bi bi-arrow-left"></i> Subira ku Dashboard
                            </Button>
                          </div>
                          {activeTab === "rukora" && <UrubyirukoRukora />}
                          {activeTab === "rudafite" && <UrubyirukoRudafiteAkazi />}
                          {activeTab === "abana" && <AbanaMirireMibi />}
                          {activeTab === "abakobwa" && <AbakobwaBabyaye />}
                          {activeTab === "abagore" && <AbagoreBatwite />}
                          {activeTab === "messaging" && <Messaging />}
                          {activeTab === "broadcasts" && <Broadcasts />}
                          {activeTab === "reports" && <ReportPage />}
                          {activeTab === "ibirego" && <AkagariIbirego />}
                          {activeTab === "isibo-ibirego" && <IbiregoAkagari />}
                          {activeTab === "imico-imyifatire" && <ImicoImyifatire mode="creator" />}
                          {activeTab === "abana-mu-mihanda" && <AbanaMuMihanda mode="creator" />}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* ═══════════ MOBILE BOTTOM NAV ═════════════════════ */}
        <div className="bottom-nav">
          {bottomNavItems.map((item) => (
            <div
              key={item.id}
              className={`bottom-nav-item ${
                item.id === 'more'
                  ? ['abakobwa', 'abagore', 'rudafite', 'reports'].includes(activeTab) ? 'active' : ''
                  : activeTab === item.id ? 'active' : ''
              }`}
              onClick={() => {
                if (item.id === 'more') {
                  setShowMobileSidebar(true);
                } else {
                  handleTabChange(item.id);
                }
              }}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
              {item.count > 0 && <span className="bnav-count">{item.count}</span>}
            </div>
          ))}
        </div>

        {/* ═══════════ FAB ═══════════════════════════════════ */}
        <div className="fab-wrapper">
          <motion.button
            className="fab-btn"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9, rotate: 45 }}
            onClick={() => setShowAdd(true)}
            aria-label="Add new record"
          >
            <i className="bi bi-plus-lg"></i>
          </motion.button>
        </div>

        {/* ═══════════ MOBILE SIDEBAR OFFCANVAS ══════════════ */}
        <Offcanvas
          show={showMobileSidebar}
          onHide={() => setShowMobileSidebar(false)}
          className="mobile-sidebar"
          style={{ background: '#090e1a', maxWidth: '85vw', width: '300px' }}
        >
          <Offcanvas.Header closeButton className="border-0" style={{ borderBottom: '1px solid var(--border)' }}>
            <Offcanvas.Title>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: 'linear-gradient(135deg, var(--accent), var(--purple))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.1rem', color: 'white'
                }}>
                  <i className="bi bi-grid-1x2-fill"></i>
                </div>
                <div>
                  <div className="fw-bold text-white" style={{ fontSize: '1rem' }}>Dashboard</div>
                  <div style={{ fontSize: '0.72rem', color: '#9db8d8' }}>Umunyamabanga</div>                </div>
              </div>
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="p-3">
            {/* Search on mobile */}
            <div className="search-container mb-4" style={{ maxWidth: '100%' }}>
              <i className="bi bi-search search-icon"></i>
              <Form.Control
                type="text"
                placeholder="Shakisha..."
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <p className="sidebar-section-title px-2">MENU</p>
            <Nav className="flex-column">
              {navItems.map((item, idx) => (
                <React.Fragment key={item.id}>
                  {item.group && item.group !== navItems[idx - 1]?.group && (
                    <p className="sidebar-section-title px-2" style={{ marginTop: '0.75rem' }}>{item.group}</p>
                  )}
                <Nav.Link
                  className={`${activeTab === item.id ? 'active' : ''} mb-1`}
                  onClick={() => handleTabChange(item.id)}
                  style={{
                    color: activeTab === item.id ? item.color : '#b8cce0',
                    background: activeTab === item.id ? `${item.color}12` : 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    fontWeight: activeTab === item.id ? 600 : 500,
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: activeTab === item.id ? `${item.color}20` : 'rgba(255,255,255,0.04)',
                    fontSize: '1rem'
                  }}>
                    <i className={item.icon}></i>
                  </div>
                  <span className="flex-grow-1">{item.label}</span>
                  {item.count > 0 && (
                    <Badge pill style={{
                      background: `${item.color}20`,
                      color: item.color,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '0.7rem'
                    }}>{item.count}</Badge>
                  )}
                </Nav.Link>
                </React.Fragment>
              ))}
            </Nav>

            <div style={{ borderTop: '1px solid var(--border)', margin: '1.5rem 0 1rem' }} />

            <button
              className="logout-sidebar-btn w-100"
              onClick={() => { setShowMobileSidebar(false); setShowLogout(true); }}
            >
              <i className="bi bi-box-arrow-right fs-5"></i>
              <span>Gusohoka</span>
            </button>
          </Offcanvas.Body>
        </Offcanvas>

        {/* ═══════════ LOGOUT MODAL ══════════════════════════ */}
        <Modal show={showLogout} onHide={() => setShowLogout(false)} centered className="custom-modal" size="sm">
          <Modal.Header closeButton>
            <Modal.Title className="text-white fs-6">
              <i className="bi bi-box-arrow-right me-2" style={{ color: 'var(--danger)' }}></i>
              Gusohoka
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center py-4">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              style={{
                width: 64, height: 64, borderRadius: 20,
                background: 'rgba(248, 81, 73, 0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem', fontSize: '1.6rem', color: 'var(--danger)'
              }}
            >
              <i className="bi bi-exclamation-triangle"></i>
            </motion.div>
            <p className="text-white mb-1 fw-bold">Urashaka gusohoka?</p>
            <p className="text-muted" style={{ fontSize: '0.85rem', color: '#9db8d8' }}>
              Uzasohoka muri sisitemu y'umunyamabanga
            </p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center gap-2 pb-4 border-0">
            <Button
              variant="outline-secondary"
              onClick={() => setShowLogout(false)}
              style={{
                borderColor: 'var(--border)',
                color: '#d8e4f8',
                borderRadius: 10,
                padding: '0.5rem 1.5rem',
                fontWeight: 600
              }}
            >
              Oya, Komeza
            </Button>
            <Button
              variant="danger"
              onClick={handleLogout}
              style={{
                background: 'var(--danger)',
                border: 'none',
                borderRadius: 10,
                padding: '0.5rem 1.5rem',
                fontWeight: 600,
                boxShadow: '0 4px 16px var(--danger-glow)'
              }}
            >
              Yego, Sohoka
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ═══════════ ADD MODAL ══════════════════════════════ */}
        <Modal show={showAdd} onHide={() => setShowAdd(false)} centered className="custom-modal">
          <Modal.Header closeButton>
            <Modal.Title className="text-white fs-6">
              <i className="bi bi-plus-circle-fill me-2" style={{ color: 'var(--accent)' }}></i>
              Ongeraho Amakuru
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-3">
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem', color: '#9db8d8' }}>
              Hitamo icyiciro ushaka kongeramo:
            </p>
            {navItems.filter(i => i.id !== 'dashboard').map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className="action-modal-item d-flex align-items-center gap-3"
                  onClick={() => { handleTabChange(item.id); setShowAdd(false); }}
                  role="button"
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: `${item.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: item.color, fontSize: '1.1rem', flexShrink: 0
                  }}>
                    <i className={item.icon}></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold" style={{ fontSize: '0.88rem' }}>{item.label}</div>
                    {item.count > 0 && (
                      <small className="text-muted">{item.count} banditswe</small>
                    )}
                  </div>
                  <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}></i>
                </div>
              </motion.div>
            ))}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   DASHBOARD CONTENT COMPONENT
════════════════════════════════════════════════════════════ */
const DashboardContent = React.memo(({ dataStats, navItems, greeting, onTabChange, onRefresh, refreshing }) => {
  return (
    <>
      {/* ═══ Page Header ═══ */}
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
          <div>
            <h2>
              <span className="greeting-wave">👋</span> {greeting}, <span style={{ color: 'var(--accent)' }}>Umunyamabanga</span>
            </h2>
            <p>Dore incamake y'amakuru y'uyu munsi</p>
          </div>
          <div style={{
            background: 'rgba(77,142,240,0.1)',
            border: '1px solid rgba(77,142,240,0.2)',
            borderRadius: 12,
            padding: '0.5rem 1rem',
            textAlign: 'right',
            flexShrink: 0
          }}>
            <div style={{ fontSize: '0.65rem', color: '#8baac8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Sisitemu</div>
            <div style={{ fontSize: '0.8rem', color: '#4d8ef0', fontWeight: 700 }}>
              <i className="bi bi-shield-check me-1"></i>Online
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═══ Stats Cards ═══ */}
      <div className="section-heading">
        <i className="bi bi-bar-chart-fill" style={{ color: 'var(--accent)' }}></i>
        Imibare Nyamukuru
      </div>
      <Row className="g-3 mb-4">
        <Col xs={6} md={6} lg={3}>
          <StatCard
            icon="bi-briefcase-fill"
            title="Urubyiruko Rukora"
            value={dataStats.youthEmployed}
            color="var(--success)"
            glowColor="var(--success-glow)"
            onClick={() => onTabChange('rukora')}
            delay={0.1}
          />
        </Col>
        <Col xs={6} md={6} lg={3}>
          <StatCard
            icon="bi-person-x-fill"
            title="Rudafite Akazi"
            value={dataStats.youthUnemployed}
            color="var(--danger)"
            glowColor="var(--danger-glow)"
            onClick={() => onTabChange('rudafite')}
            delay={0.2}
          />
        </Col>
        <Col xs={6} md={6} lg={3}>
          <StatCard
            icon="bi-person-hearts"
            title="Abakobwa Babyaye"
            value={dataStats.teenMothers}
            color="var(--purple)"
            glowColor="var(--purple-glow)"
            onClick={() => onTabChange('abakobwa')}
            delay={0.3}
          />
        </Col>
        <Col xs={6} md={6} lg={3}>
          <StatCard
            icon="bi-heart-pulse-fill"
            title="Mirire Mibi"
            value={dataStats.malnutritionCases}
            color="var(--warning)"
            glowColor="var(--warning-glow)"
            onClick={() => onTabChange('abana')}
            delay={0.4}
          />
        </Col>
      </Row>

      {/* ═══ Additional Mini Stats ═══ */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="glass-card p-3" style={{ cursor: 'pointer', borderColor: 'rgba(57,210,192,0.2)', borderTop: '2px solid var(--teal)' }} onClick={() => onTabChange('abagore')}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: 'rgba(57, 210, 192, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--teal)', fontSize: '1.1rem'
                }}>
                  <i className="bi bi-person-standing-dress"></i>
                </div>
                <div>
                  <div className="stat-card-title">Abagore Batwite</div>
                  <div className="fw-bold text-white fs-5">
                    <AnimatedCounter value={dataStats.maternalCases} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Col>
        <Col xs={6} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <div className="glass-card p-3" style={{ borderColor: 'rgba(88,166,255,0.2)', borderTop: '2px solid var(--accent)' }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: 'rgba(88, 166, 255, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--accent)', fontSize: '1.1rem'
                }}>
                  <i className="bi bi-people-fill"></i>
                </div>
                <div>
                  <div className="stat-card-title">Urubyiruko Rwose</div>
                  <div className="fw-bold text-white fs-5">
                    <AnimatedCounter value={dataStats.youthTotal} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Col>
        <Col xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="glass-card p-3" style={{ borderColor: 'rgba(248,81,73,0.2)', borderTop: '2px solid var(--danger)' }}>
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: 'rgba(248, 81, 73, 0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--danger)', fontSize: '1.1rem'
                }}>
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </div>
                <div>
                  <div className="stat-card-title">Byihutirwa</div>
                  <div className="fw-bold text-white fs-5">
                    <AnimatedCounter value={dataStats.malnourishmentUrgent} />
                  </div>
                </div>
              </div>
              <div className="mini-stat-bar mt-2">
                <motion.div
                  className="mini-stat-bar-fill"
                  style={{ background: 'var(--danger)' }}
                  initial={{ width: 0 }}
                  animate={{
                    width: `${dataStats.malnutritionCases > 0
                      ? (dataStats.malnourishmentUrgent / dataStats.malnutritionCases) * 100
                      : 0}%`
                  }}
                  transition={{ duration: 1.5, delay: 0.8 }}
                />
              </div>
            </div>
          </motion.div>
        </Col>
      </Row>

      {/* ═══ Progress Charts ═══ */}
      <div className="section-heading">
        <i className="bi bi-pie-chart-fill" style={{ color: 'var(--purple)' }}></i>
        Imibare y'Iterambere
      </div>
      <Row className="g-3 mb-4">
        <Col md={6}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            <Card className="glass-card progress-insight-card h-100">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="fw-bold text-white mb-1">Igipimo Cy'Akazi</h6>
                    <small className="text-muted" style={{ color: '#9db0c8' }}>Mu rubyiruko rwanditswe</small>
                  </div>
                  <Badge style={{
                    background: 'rgba(63, 185, 80, 0.12)',
                    color: 'var(--success)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    padding: '0.4em 0.8em'
                  }}>
                    <i className="bi bi-arrow-up-short"></i>
                    {dataStats.employmentRate.toFixed(1)}%
                  </Badge>
                </div>

                <Row className="align-items-center">
                  <Col xs={5} className="text-center">
                    <CircularProgress
                      percentage={dataStats.employmentRate}
                      color="var(--success)"
                      size={110}
                    />
                  </Col>
                  <Col xs={7}>
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="status-dot online"></span>
                        <small className="text-muted" style={{ color: '#9db0c8' }}>Bafite akazi</small>
                      </div>
                      <div className="fw-bold text-white">{dataStats.youthEmployed}</div>
                      <div className="mini-stat-bar">
                        <motion.div
                          className="mini-stat-bar-fill"
                          style={{ background: 'var(--success)' }}
                          initial={{ width: 0 }}
                          animate={{ width: `${dataStats.employmentRate}%` }}
                          transition={{ duration: 1.5, delay: 1 }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="status-dot danger"></span>
                        <small className="text-muted" style={{ color: '#9db0c8' }}>Badafite akazi</small>
                      </div>
                      <div className="fw-bold text-white">{dataStats.youthUnemployed}</div>
                      <div className="mini-stat-bar">
                        <motion.div
                          className="mini-stat-bar-fill"
                          style={{ background: 'var(--danger)' }}
                          initial={{ width: 0 }}
                          animate={{ width: `${100 - dataStats.employmentRate}%` }}
                          transition={{ duration: 1.5, delay: 1.2 }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        <Col md={6}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            <Card className="glass-card progress-insight-card h-100">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="fw-bold text-white mb-1">Abasubiye Mu Ishuri</h6>
                    <small className="text-muted" style={{ color: '#9db0c8' }}>Abakobwa babyaye basubiye</small>
                  </div>
                  <Badge style={{
                    background: 'rgba(188, 140, 255, 0.12)',
                    color: 'var(--purple)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    padding: '0.4em 0.8em'
                  }}>
                    <i className="bi bi-mortarboard-fill me-1"></i>
                    {dataStats.schoolReturnRate.toFixed(1)}%
                  </Badge>
                </div>

                <Row className="align-items-center">
                  <Col xs={5} className="text-center">
                    <CircularProgress
                      percentage={dataStats.schoolReturnRate}
                      color="var(--purple)"
                      size={110}
                    />
                  </Col>
                  <Col xs={7}>
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="status-dot" style={{ background: 'var(--purple)', boxShadow: '0 0 8px var(--purple-glow)' }}></span>
                        <small className="text-muted" style={{ color: '#9db0c8' }}>Mu ishuri</small>
                      </div>
                      <div className="fw-bold text-white">{dataStats.mothersInSchool}</div>
                      <div className="mini-stat-bar">
                        <motion.div
                          className="mini-stat-bar-fill"
                          style={{ background: 'var(--purple)' }}
                          initial={{ width: 0 }}
                          animate={{ width: `${dataStats.schoolReturnRate}%` }}
                          transition={{ duration: 1.5, delay: 1 }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="status-dot" style={{ background: 'var(--text-muted)' }}></span>
                        <small className="text-muted" style={{ color: '#9db0c8' }}>Batari mu ishuri</small>
                      </div>
                      <div className="fw-bold text-white">{dataStats.teenMothers - dataStats.mothersInSchool}</div>
                      <div className="mini-stat-bar">
                        <motion.div
                          className="mini-stat-bar-fill"
                          style={{ background: 'var(--text-muted)' }}
                          initial={{ width: 0 }}
                          animate={{ width: `${100 - dataStats.schoolReturnRate}%` }}
                          transition={{ duration: 1.5, delay: 1.2 }}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* ═══ Quick Actions ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h6 className="fw-bold text-white mb-3">
          <i className="bi bi-lightning-charge-fill me-2" style={{ color: 'var(--warning)' }}></i>
          Ibikorwa Byihuse
        </h6>
        <Row className="g-2 mb-4">
          {[
            { icon: 'bi-plus-circle', label: 'Ongeraho', color: 'var(--accent)', action: 'add' },
            { icon: 'bi-arrow-clockwise', label: 'Kuvugurura', color: 'var(--success)', action: 'refresh' },
            { icon: 'bi-megaphone', label: 'Amatangazo', color: 'var(--cyan, #56d4dd)', action: 'broadcasts' },
            { icon: 'bi-file-earmark-text', label: 'Raporo', color: 'var(--indigo, #6366f1)', action: 'reports' },
            { icon: 'bi-download', label: 'Gukuramo', color: 'var(--purple)', action: 'export' },
            { icon: 'bi-printer', label: 'Gucapa', color: 'var(--teal)', action: 'print' },
          ].map((action, idx) => (
            <Col xs={4} sm={4} md={2} key={idx}>
              <motion.div
                whileTap={{ scale: 0.95 }}
                className="quick-action-btn"
                onClick={() => {
                  if (action.action === 'refresh') onRefresh();
                  else if (action.action === 'broadcasts') onTabChange('broadcasts');
                  else if (action.action === 'reports') onTabChange('reports');
                }}
              >
                <div className="qa-icon" style={{ background: `${action.color}15`, color: action.color }}>
                  <i className={action.icon}></i>
                </div>
                <span className="qa-label">{action.label}</span>
              </motion.div>
            </Col>
          ))}
        </Row>
      </motion.div>

      {/* ═══ Action Items / Alerts ═══ */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
      >
        <Card className="glass-card">
          <Card.Body className="p-3 p-md-4">
            <h6 className="fw-bold text-white mb-3">
              <i className="bi bi-bell-fill me-2" style={{ color: 'var(--accent)' }}></i>
              Amakuru y'Ingenzi
            </h6>

            <div
              className="action-card ripple-effect"
              onClick={() => onTabChange('abagore')}
            >
              <div className="d-flex align-items-center flex-grow-1 min-w-0">
                <div className="action-dot" style={{ background: 'var(--teal)' }}></div>
                <div className="action-label">
                  <strong>{dataStats.maternalCases} Abagore batwite</strong>
                  <br />
                  <small>{dataStats.maternalCheckedUp} basuzumwe na muganga</small>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)' }}></i>
            </div>

            <div
              className="action-card ripple-effect"
              onClick={() => onTabChange('rudafite')}
            >
              <div className="d-flex align-items-center flex-grow-1 min-w-0">
                <div className="action-dot" style={{ background: 'var(--danger)' }}></div>
                <div className="action-label">
                  <strong>{dataStats.youthUnemployed} Urubyiruko rudafite akazi</strong>
                  <br />
                  <small>Bakeneye ubufasha bwo kwifashisha</small>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)' }}></i>
            </div>

            {dataStats.malnourishmentUrgent > 0 && (
              <div
                className="action-card ripple-effect"
                onClick={() => onTabChange('abana')}
                style={{ borderColor: 'rgba(248, 81, 73, 0.2)' }}
              >
                <div className="d-flex align-items-center flex-grow-1 min-w-0">
                  <div className="action-dot" style={{
                    background: 'var(--danger)',
                    animation: 'notifPulse 1.5s ease-in-out infinite'
                  }}></div>
                  <div className="action-label">
                    <strong style={{ color: 'var(--danger)' }}>
                      ⚠️ {dataStats.malnourishmentUrgent} Abana bakeneye ubufasha bwihutirwa
                    </strong>
                    <br />
                    <small>Ibibazo by'imirire bikabije</small>
                  </div>
                </div>
                <i className="bi bi-chevron-right" style={{ color: 'var(--danger)' }}></i>
              </div>
            )}

            <div
              className="action-card ripple-effect"
              onClick={() => onTabChange('abakobwa')}
            >
              <div className="d-flex align-items-center flex-grow-1 min-w-0">
                <div className="action-dot" style={{ background: 'var(--purple)' }}></div>
                <div className="action-label">
                  <strong>{dataStats.teenMothers} Abakobwa babyaye</strong>
                  <br />
                  <small>{dataStats.mothersInSchool} basubiye mu ishuri</small>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)' }}></i>
            </div>

            {/* Quick link to Broadcasts from alerts */}
            <div
              className="action-card ripple-effect"
              onClick={() => onTabChange('broadcasts')}
            >
              <div className="d-flex align-items-center flex-grow-1 min-w-0">
                <div className="action-dot" style={{ background: 'var(--cyan, #56d4dd)' }}></div>
                <div className="action-label">
                  <strong>📢 Amatangazo</strong>
                  <br />
                  <small>Reba amatangazo mashya cyangwa wandike itangazo</small>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)' }}></i>
            </div>

            {/* Quick link to Report from alerts */}
            <div
              className="action-card ripple-effect"
              onClick={() => onTabChange('reports')}
            >
              <div className="d-flex align-items-center flex-grow-1 min-w-0">
                <div className="action-dot" style={{ background: 'var(--indigo, #6366f1)' }}></div>
                <div className="action-label">
                  <strong>📝 Kwandika Raporo</strong>
                  <br />
                  <small>Andika raporo nshya y'umudugudu</small>
                </div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: 'var(--text-muted)' }}></i>
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* ═══ Footer Info ═══ */}
      <motion.div
        className="text-center mt-4 pb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border)',
          borderRadius: 20, padding: '0.4rem 1.25rem'
        }}>
          <span className="status-dot online"></span>
          <small style={{ fontSize: '0.72rem', color: '#8b9ab5' }}>
            Sisitemu y'Umunyamabanga w'Akagari &bull; {new Date().getFullYear()}
          </small>
        </div>
      </motion.div>
    </>
  );
});

export default DashboardUmunyamabanga;